import {
  sendNotification,
  useApiClient,
  useEntityMetadataIncludeExternal,
  useModuleBaseMetadata,
  useTranslation,
} from '@msdk/common'
import {
  Entity,
  EntityMetadata,
  EntityOperation,
  FieldDefinition,
  FormEntityViewDefinition,
  FormFieldDefinition,
  FormNode,
} from '@msdk/definitions'
import { ButtonIcon, Card, COLORS, Divider, IconSelector, styled, Typography } from '@msdk/ui'
import React, { useEffect, useMemo, useState } from 'react'

import { useFormModal } from '../..'

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: calc(100% - 24px);
  height: fit-content;
  padding: 16px;
  gap: 24px;
  border-radius: 16px;
  border: 1px solid ${COLORS.grey.cardBorder};
  margin-bottom: 24px;
`

const StyledTitle = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const StyledBody = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: start;
  gap: 16px;
  width: 100%;
`

const Row = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
  width: 100%;
`

const Cell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: start;
  flex: 1;
  padding: 0 8px;
`

interface Props {
  node: FormNode
  entity: Entity | null
}

export function EntityView({ node, entity }: Props) {
  const entityViewDef = node.value.formDefinition as FormEntityViewDefinition
  const t = useTranslation()
  const client = useApiClient()
  const [targetEntity, setTargetEntity] = useState<Entity | null>(null)
  const [canUpdate, setCanUpdate] = useState(false)
  const modal = useFormModal()

  const baseMetadata = useModuleBaseMetadata(entityViewDef.entityName, entityViewDef.editForm) as
    | EntityMetadata
    | undefined

  const targetEntityModel = useEntityMetadataIncludeExternal(entityViewDef.entityName, entityViewDef.editForm)

  const msProvider = baseMetadata?.msProvider

  const fieldDefs = useMemo(() => {
    if (!targetEntityModel) return []

    const formFields = (entityViewDef.fields ?? []) as FormFieldDefinition[]

    return formFields
      .map((f) => targetEntityModel.getFieldDefinition(f.name))
      .filter((fd): fd is FieldDefinition => !!fd)
  }, [entityViewDef.fields, targetEntityModel])

  useEffect(() => {
    if (!msProvider) {
      sendNotification({
        message: t('NOT_FOUND_MS_METADATA_ERROR', 'Some data was not able to be loaded, a microservice is missing'),
        status: 'error',
      })
    }
  }, [msProvider])

  /**
   * Resolved parentId. The expected formats from props are either:
   * - "entity.id" (the current entity's id)
   * - "entity.<fieldName>" (a field value from the current entity)
   * - a literal id
   */
  const resolvedParentId = useMemo(() => {
    if (!entity || !entityViewDef.parentId) return null
    /**
     *  "entity.id" means the current entity's id
     */
    if (entityViewDef.parentId === 'entity.id') return entity.id
    /**
     * If it's "entity.<fieldName>", resolve from entity.fields
     */
    const match = entityViewDef.parentId.match(/^entity\.(.+)$/)
    if (match) {
      const fieldName = match[1]
      return fieldName === 'id' ? entity.id : entity.fields[fieldName]?.value?.toString() ?? null
    }
    /**
     * As a fallback, treat as literal id
     */
    return entityViewDef.parentId
  }, [entity, entityViewDef.parentId])

  const resolvedEntityId = useMemo(() => {
    if (!entity || !entityViewDef.entityId) return null
    /**
     *  "entity.id" means the current entity's id
     */
    if (entityViewDef.entityId === 'entity.id') return entity.id
    /**
     * If it's "entity.<fieldName>", resolve from entity.fields
     */
    const match = entityViewDef.entityId.match(/^entity\.(.+)$/)
    if (match) {
      const fieldName = match[1]
      return fieldName === 'id' ? entity.id : entity.fields[fieldName]?.value?.toString() ?? null
    }
    /**
     * As a fallback, treat as literal id
     */
    return entityViewDef.entityId
  }, [entity, entityViewDef.entityId])

  /**
   * Fetch the target entityusing parentId
   */
  useEffect(() => {
    if ((!resolvedParentId && !resolvedEntityId) || !targetEntityModel || !msProvider) return

    fetchEntity()
  }, [resolvedParentId, resolvedEntityId, targetEntityModel, msProvider, client, entity])

  const fetchEntity = () => {
    if ((!resolvedParentId && !resolvedEntityId) || !targetEntityModel || !msProvider) return

    const fetchData = async () => {
      try {
        let res: EntityOperation

        if (targetEntityModel.singleton) {
          if (!resolvedParentId) {
            console.error(
              '[ERROR] EntityView found a singleton entity but parentId is null. Check if parentId in the form definition is correctly set.',
            )
            return
          }

          res = await client.entity.getSingleton(msProvider, resolvedParentId, targetEntityModel)
          if (res.entities.length > 0) {
            setTargetEntity(res.entities[0])
            setCanUpdate(res?.metadataList?.[0].canUpdate)
          }
        } else {
          /**
           * For non-singleton entities we need to fetch by the resolvedEntityId which is resolved from the current entity's fields.
           */
          if (!resolvedEntityId) {
            console.error(
              '[ERROR] EntityView found a non-singleton entity but entityId is null. Check if entityId in the form definition is correctly set.',
            )
            return
          }
          res = await client.entity.getEntity(msProvider, resolvedEntityId, targetEntityModel)
          if (res.entities.length > 0) {
            setTargetEntity(res.entities[0])
            setCanUpdate(res?.metadataList?.[0].canUpdate)
          }
        }
      } catch (e) {
        console.error(e)
      }
    }

    fetchData()
  }

  const handleEditClick = () => {
    let editId: string | null = null

    if (targetEntityModel?.singleton) {
      if (!resolvedParentId) return
      editId = resolvedParentId
    } else {
      editId = targetEntity ? targetEntity.id : 'Create'
    }

    if (!editId) return

    const m = modal.createModal(
      entityViewDef.entityName,
      entityViewDef.editForm,
      editId,
      () => {
        fetchEntity()
      },
      { mode: 'Edit', containerEntity: entity ?? undefined, modalForm: entityViewDef.modalForm },
    )
    m.open()
  }

  return (
    <StyledCard>
      <StyledTitle>
        <Typography variant="subtitle2">
          {entityViewDef.idml && entityViewDef.description
            ? t(entityViewDef.idml, entityViewDef.description).toUpperCase()
            : targetEntityModel?.idml && targetEntityModel?.description
            ? t(targetEntityModel.idml, targetEntityModel.description).toUpperCase()
            : '—'}
        </Typography>
        {canUpdate && entityViewDef.editForm && (
          <ButtonIcon onClick={handleEditClick}>
            <IconSelector iconName="EditOutlined" color="#0342C9" />
          </ButtonIcon>
        )}
      </StyledTitle>
      <StyledBody>
        {fieldDefs.map((fieldDef, i) => {
          const fieldValue = targetEntity?.fields[fieldDef.name]
          return (
            <React.Fragment key={fieldDef.name}>
              <Row>
                <Cell>
                  <Typography variant="body1" color={COLORS.grey.label}>
                    {fieldDef.idml && fieldDef.description ? t(fieldDef.idml, fieldDef.description) : fieldDef.name}
                  </Typography>
                </Cell>
                <Cell>
                  <Typography variant="body1">{fieldValue ? fieldValue.toStringForView() : '—'}</Typography>
                </Cell>
              </Row>
              {i < fieldDefs.length - 1 && (
                <Divider
                  key={`divider-${fieldDef.name}`}
                  orientation="horizontal"
                  flexItem
                  color={COLORS.grey.cardBorder}
                />
              )}
            </React.Fragment>
          )
        })}
      </StyledBody>
    </StyledCard>
  )
}
